import type { AnatomyBinding } from "./componentAnatomy";
import { avatarGroupAnatomySizes } from "./componentAnatomyAvatarSizes";

type GroupSize = (typeof avatarGroupAnatomySizes)[number];

/**
 * AvatarGroup seam bindings per shape, as the `.item` rules in
 * AvatarGroup.module.scss declare them. The x-ray stack repeats one shape, so
 * each variant shows the cut its own shape gives the avatar before it.
 */

export type SizeBindings = Readonly<Record<string, readonly AnatomyBinding[]>>;

const itemEdge = "calc(100% - var(--avatar-group-overlap))";
const seam = "var(--avatar-group-seam)";
const cornerCut = "var(--avatar-group-corner)";

const roundCut = ({ radius }: GroupSize): readonly AnatomyBinding[] => [
    {
        partId: "seam",
        property: "round cut radius",
        literal: `calc(${radius} + ${seam})`,
        via: "--avatar-group-cut-r",
        note: "The next circle grown by the seam, so every avatar keeps its full size and the gap sits outside the next one.",
    },
    {
        partId: "seam",
        property: "mask-image",
        literal: `radial-gradient(circle at calc(${itemEdge} + ${radius}) 50%, transparent var(--avatar-group-cut-r), black calc(var(--avatar-group-cut-r) + 0.5px))`,
        note: "Round cut when the next avatar is a circle. .item:not(:last-child).",
    },
];

const squareCut = (): readonly AnatomyBinding[] => [
    {
        partId: "seam",
        property: "corner cut radius",
        literal: `calc(var(--radius-xs) + ${seam})`,
        via: "--avatar-group-corner",
        note: "The next square's --radius-xs corner (spec B1) grown by the seam.",
    },
    {
        partId: "seam",
        property: "mask-image",
        literal: `linear-gradient(to right, black calc(${itemEdge} - ${seam}), transparent 0), radial-gradient(circle at 100% 100%, transparent ${cornerCut}, black calc(${cornerCut} + 0.5px)), radial-gradient(circle at 100% 0, transparent ${cornerCut}, black calc(${cornerCut} + 0.5px))`,
        note: 'Square cut when the next avatar is a square: everything left of its edge less the seam, plus what lies outside its two grown corners. A circle before a square is cut flat too, as the old IconList drew it. .item:has(+ .item [data-shape="square"]).',
    },
    {
        partId: "seam",
        property: "mask-position",
        literal: "0 0, 0 0, 0 100%",
        note: "The corner layers sit at the top and bottom of the item.",
    },
    {
        partId: "seam",
        property: "mask-size",
        literal: `100% 100%, calc(${itemEdge} + var(--radius-xs)) var(--radius-xs), calc(${itemEdge} + var(--radius-xs)) var(--radius-xs)`,
        note: "Each corner layer ends at the centre of the next square's corner, with mask-repeat: no-repeat.",
    },
];

const diamondEdge = "var(--avatar-group-diamond-edge)";
const diamondCentre = "var(--avatar-group-diamond-centre)";

const diamondCut = ({
    radius,
    diamondOverlap,
}: GroupSize): readonly AnatomyBinding[] => [
    {
        partId: "seam",
        property: "overlap",
        ...diamondOverlap,
        via: "--avatar-group-overlap",
        note: "Class (diamond) stacks don't overlap at xs, where letters reach the tips, and share the group overlap otherwise (spec B7). Applied as a negative margin-inline-start.",
    },
    {
        partId: "seam",
        property: "diamond edge",
        literal: `calc((3 * ${radius} - var(--avatar-group-overlap) - var(--radius-xs) * 0.4142) * 0.7071 - ${seam})`,
        via: "--avatar-group-diamond-edge",
        note: "Where the next diamond's 45deg edges, pushed out by the seam, cross each gradient line. Its rounded tip touches its box, so its sharp corner sits radius-xs * (sqrt(2) - 1) left of the edge.",
    },
    {
        partId: "seam",
        property: "rounding centre",
        literal: `calc(${itemEdge} + var(--radius-xs))`,
        via: "--avatar-group-diamond-centre",
        note: "Centre of the next diamond's tip rounding, a --radius-xs circle grown by the seam.",
    },
    {
        partId: "seam",
        property: "mask-image",
        literal: `linear-gradient(135deg, black calc(${diamondEdge} - 0.5px), transparent ${diamondEdge}), linear-gradient(45deg, black calc(${diamondEdge} - 0.5px), transparent ${diamondEdge}), radial-gradient(circle at ${diamondCentre} 50%, transparent ${cornerCut}, black calc(${cornerCut} + 0.5px)), conic-gradient(from 225deg at ${diamondCentre} 50%, black 90deg, transparent 0)`,
        note: 'Diamond cut when the next avatar is a diamond: .item:has(+ .item [data-shape="diamond"]). ds-deviation: Figma draws no class stack (spec B7).',
    },
    {
        partId: "seam",
        property: "mask-composite",
        literal: "add, add, intersect, add",
        note: "Keeps what lies left of either 45deg edge, plus what lies outside the rounding circle within the point's 90deg cone.",
    },
];

const groupSizeBindings = (
    cut: (groupSize: GroupSize) => readonly AnatomyBinding[],
): SizeBindings =>
    Object.fromEntries(
        avatarGroupAnatomySizes.map((groupSize) => [
            groupSize.id,
            cut(groupSize),
        ]),
    );

export const avatarSeamBindings = {
    circle: groupSizeBindings(roundCut),
    square: groupSizeBindings(squareCut),
    diamond: groupSizeBindings(diamondCut),
} as const;
